'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Icon } from '@/components/icon'
import { MaxWidthWrapper } from '@/components/max-width-wrapper'
import { cn } from '@/lib/utils'

const DISMISS_KEY = 'announcement_free_access_dismissed_until'

export function AnnouncementBanner({
  className,
  isActive,
  label,
  periodKey,
}: {
  className?: string
  isActive: boolean
  label: string
  periodKey: string
}) {
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    if (!isActive) {
      setDismissed(true)
      return
    }

    try {
      const dismissedUntil = localStorage.getItem(DISMISS_KEY)
      setDismissed(dismissedUntil === periodKey)
    } catch {
      setDismissed(false)
    }
  }, [isActive, periodKey])

  if (!isActive || dismissed) return null

  return (
    <div
      className={cn(
        [
          'fixed inset-x-0 top-(--navbar-height) z-50 border-b text-christ-white-900 backdrop-blur-md',
          'bg-christ-black-900/98',
          'bg-linear-to-r from-christ-red/14 via-christ-black-900/98 to-christ-black-900/98',
          'border-christ-red/20 shadow-[0_10px_24px_rgba(0,0,0,0.45)]',
        ],
        className,
      )}
    >
      <MaxWidthWrapper size='xl' className='py-2.5'>
        <div className='flex items-center gap-3'>
          <p className='min-w-0 flex-1 text-sm text-white/90 leading-snug'>
            {(() => {
              const cta = 'Watch now'
              const idx = label.indexOf(cta)
              if (idx === -1) {
                return <span className='font-semibold text-white'>{label}</span>
              }

              const before = label.slice(0, idx)
              const after = label.slice(idx + cta.length)

              return (
                <span className='font-semibold text-white'>
                  {before}
                  <Link href='/browse' className='underline underline-offset-2 hover:no-underline'>
                    {cta}
                  </Link>
                  {after}
                </span>
              )
            })()}
          </p>

          <div className='flex shrink-0 items-center gap-2'>
            <button
              type='button'
              className='inline-flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white'
              aria-label='Dismiss announcement'
              onClick={() => {
                try {
                  localStorage.setItem(DISMISS_KEY, periodKey)
                } catch {}
                setDismissed(true)
              }}
            >
              <Icon name='close' className='size-4' />
            </button>
          </div>
        </div>
      </MaxWidthWrapper>
    </div>
  )
}
